import { type Type } from '@angular/core';
import { type OmitIndexSignature } from 'type-fest';

import {
  type CanvasElementType,
  type DataProps,
  type PartialPropsOf,
} from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDataProperties,
  mergeDataBindingsValues,
  type OptionPickerDefaultValue,
  type PropertiesConfigInputVisualEffectModel,
  type ToggleButtonStateKey,
  type WithBulkActions,
} from '@blaze-shared/elements-domain';
import { deepClone } from '@blaze-shared/shared-util';

import {
  type ParentChildrenContentOverrides,
  type ParentChildrenStyleOverrides,
} from '../../canvas-elements/parent-overrides';

export interface PropsBuilderConfig {
  /** If child builder reports that the element supports data binding, common data binding functions will be applied. */
  hasData?: boolean;
  hasLabel?: boolean;
}

export class PropsBuilder<T extends CanvasElementType> {
  private props: PartialPropsOf<T>;

  setPlaceholder: (placeholder: string | undefined) => this;
  setDefaultValue: (defaultValue: OptionPickerDefaultValue | undefined) => this;
  /** Initial state of a two-state (toggle) element; NOOP for elements without states. */
  setInitialToggleState: (state: ToggleButtonStateKey | undefined) => this;
  setBinding: (binding: CanvasElementDataProperties | undefined, keepDeleted?: boolean) => this;
  setLabel: (label: string, enable?: boolean) => this;
  setMargin: (margin: string | undefined) => this;
  setVisualEffect: (visualEffect: PropertiesConfigInputVisualEffectModel | undefined) => this;
  /** passing overrides = null clears the defined props values */
  setParentContentProps: (
    elementProps: PartialPropsOf<T>,
    overrides: ParentChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<T>,
  ) => this;
  /** passing overrides = null clears the defined props values */
  setParentStyleProps: (
    elementProps: PartialPropsOf<T>,
    overrides: ParentChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<T>,
  ) => this;
  setReadonly: (readonly: boolean) => this;
  removeBulkAction: (action: keyof WithBulkActions) => this;

  // Returns the reference to this builder and takes no action
  readonly NOOP = (): this => this;
  constructor(props: PartialPropsOf<T> | undefined, config?: PropsBuilderConfig) {
    this.props = deepClone(props || ({} as PartialPropsOf<T>));

    // Default behavior is no action
    this.setPlaceholder = this.NOOP;
    this.setDefaultValue = this.NOOP;
    this.setInitialToggleState = this.NOOP;
    this.setBinding = this.NOOP;
    this.setLabel = this.NOOP;
    this.setMargin = this.NOOP;
    this.setVisualEffect = this.NOOP;
    this.setParentContentProps = this.NOOP;
    this.setParentStyleProps = this.NOOP;
    this.setReadonly = this.NOOP;
    this.removeBulkAction = this.NOOP;

    // Apply config customizations
    if (config?.hasData) {
      this.dataFnsApply();
    }
    if (config?.hasLabel) {
      this.labelFnsApply();
    }
  }

  build(): PartialPropsOf<T> {
    return deepClone(this.props);
  }

  protected patchProps(patch: OmitIndexSignature<PartialPropsOf<T>>): this {
    this.props = applyPropsPatch<T>(this.props, patch);
    return this;
  }

  protected labelFnsSetLabel(label: string, enable?: boolean): this {
    // If enable param provided - set it to passed value
    // If undefined, decide based on whether the label is falsy or not
    const enabled = enable !== undefined ? enable : !!label;

    this.patchProps({ content: { title: { label, enabled } } } as PartialPropsOf<T>);
    return this;
  }

  /** Applies implementations with the knowledge that the data properties are reported by the prop builder */
  private dataFnsApply(): void {
    this.setBinding = this.dataFnsSetBinding;
  }

  private dataFnsSetBinding(
    binding: CanvasElementDataProperties | undefined,
    keepDeleted = false,
  ): this {
    if (!binding) {
      return this;
    }

    const props = this.props as PartialPropsOf<T> & DataProps;
    const bindings = mergeDataBindingsValues(props.data || [], [binding], keepDeleted);
    this.patchProps({ data: bindings } as PartialPropsOf<T> & DataProps);
    return this;
  }

  private labelFnsApply(): void {
    this.setLabel = this.labelFnsSetLabel;
  }
}

declare module '../../canvas-elements/element-definition' {
  interface CanvasElementDefinition<
    T extends CanvasElementType = CanvasElementType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TVariant extends CanvasElementType = T,
  > {
    /** Props Builder implementation class */
    propsBuilder?: Type<PropsBuilder<T>>;
  }
}
